import { CheckCircle } from 'lucide-react';
import React, { Suspense, useState } from 'react';

import { useSelectedTab } from '../store/hooks';
import { NarrativeState } from '../types/narrative';
import { canFinalize } from '../utils/narrativeHelpers';

import { FinalizeModal } from './FinalizeModal';
import { NarrativeTreeView } from './NarrativeTreeView';

// Lazy load PlanningNotes since it's not always shown
const PlanningNotes = React.lazy(() =>
  import('./PlanningNotes').then((module) => ({ default: module.PlanningNotes }))
);

interface NarrativePreviewProps {
  narrativeState: NarrativeState;
  onNodeClick: (nodeId: string) => void;
}

export const NarrativePreview = React.memo(
  function NarrativePreview({ narrativeState, onNodeClick }: NarrativePreviewProps) {
    const activeTab = useSelectedTab();
    const [showFinalizeModal, setShowFinalizeModal] = useState(false);

    const currentNode = narrativeState.nodes[narrativeState.currentNodeId];
    const canFinalizeStory = canFinalize(narrativeState);

    if (!currentNode) {
      return (
        <div className="h-full flex items-center justify-center text-gray-500">
          <p>No node selected</p>
        </div>
      );
    }

    const handleFinalize = () => {
      setShowFinalizeModal(true);
    };

    const handleConfirmFinalize = () => {
      // This could trigger export or completion flow
      setShowFinalizeModal(false);
      console.log('Story finalized!');
    };

    return (
      <div className="h-full flex flex-col">
        {/* Tab Content */}
        <div className="flex-1 overflow-hidden">
          {activeTab === 'tree' && (
            <div className="h-full p-6 overflow-y-auto">
              <div className="max-w-6xl mx-auto">
                {/* Segment tree view */}
                <NarrativeTreeView
                  narrativeState={narrativeState}
                  currentNodeId={narrativeState.currentNodeId}
                  segmentId={currentNode.segmentId}
                  onNodeClick={onNodeClick}
                />

                {/* Finalization Button */}
                {canFinalizeStory && (
                  <div className="mt-8 flex justify-center">
                    <button
                      onClick={handleFinalize}
                      className="relative group px-8 py-4 bg-gradient-to-r from-green-600 to-green-500 text-white font-bold rounded-xl shadow-2xl hover:shadow-green-500/25 transition-all duration-300 hover:scale-105 animate-pulse"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-green-400 to-green-300 rounded-xl blur-lg opacity-30 group-hover:opacity-50 transition-opacity" />
                      <div className="relative flex items-center gap-3">
                        <CheckCircle className="w-6 h-6" />
                        <span className="text-lg">Finalise Story</span>
                      </div>
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
          {activeTab === 'planning' && (
            <Suspense
              fallback={
                <div className="h-full flex items-center justify-center text-gray-500">
                  <p>Loading planning notes...</p>
                </div>
              }
            >
              <PlanningNotes />
            </Suspense>
          )}
        </div>

        {/* Finalize Modal */}
        <FinalizeModal
          isOpen={showFinalizeModal}
          onClose={() => setShowFinalizeModal(false)}
          onConfirm={handleConfirmFinalize}
        />
      </div>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison
    return (
      prevProps.onNodeClick === nextProps.onNodeClick &&
      prevProps.narrativeState.currentNodeId === nextProps.narrativeState.currentNodeId &&
      prevProps.narrativeState.currentSegmentId === nextProps.narrativeState.currentSegmentId &&
      prevProps.narrativeState.nodes === nextProps.narrativeState.nodes
    );
  }
);
